import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator.tsx';

import type { ListLalSegment } from '@openx/types/segments.ts';
import type { Columns } from '@openx/types/tableHelpers.ts';
import type { I18nTranslate } from '@openx/types/translations.ts';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber.ts';
import { MIN_AUC_SCORE_THRESHOLD } from '../../constants.ts';
import { DateCell } from '../SegmentsList/parts/DateCell.tsx';
import { mapStatus } from '../utils/mapLalStatus.ts';
import { NameCell } from './parts/NameCell.tsx';
import { OwnerCell } from './parts/OwnerCell.tsx';
import { ReachCell } from './parts/ReachCell.tsx';
import { SeedCell } from './parts/SeedCell.tsx';

export const getListTableColumns = (t: I18nTranslate): Columns<ListLalSegment> => {
  return [
    {
      key: 'lookalike_name',
      render: lalSegment => <NameCell lalSegment={lalSegment} />,
      title: t('Name'),
      width: '25%',
      minWidth: '150px',
    },
    {
      key: 'status',
      render: ({ status: itemStatus }) => {
        const status = mapStatus(itemStatus);

        if (!status) {
          return null;
        }

        return <StatusIndicator label={status.label} status={status.status} />;
      },
      title: t('Status'),
      width: '14%',
      minWidth: '80px',
    },
    {
      key: 'derived_balanced_reach',
      render: ({ lookalike_segment_similarities }) =>
        lookalike_segment_similarities ? <ReachCell similarities={lookalike_segment_similarities} /> : null,
      title: t('Reach'),
      width: '15%',
      minWidth: '90px',
    },
    {
      key: 'auc_score',
      render: ({ auc_score }) => (auc_score ? abbreviatedNumber(auc_score, { fractionSize: 2 }) : null),
      title: t('LAL Quality Score'),
      width: '16%',
      minWidth: '120px',
      tooltipMessage: t(`LAL Quality Score must meet {minAucScore} threshold for LAL Segment generation & targeting.`, {
        minAucScore: MIN_AUC_SCORE_THRESHOLD,
      }),
      hide: true,
    },
    {
      key: 'seed',
      render: ({ lookalike_segment_seed_aggregate }) =>
        lookalike_segment_seed_aggregate ? <SeedCell seed={lookalike_segment_seed_aggregate} /> : null,
      title: t('Seed'),
      width: '17%',
      minWidth: '110px',
    },
    {
      key: 'owner',
      render: ({ account, provider }) => <OwnerCell account={account} provider={provider} />,
      title: t('Owner'),
      width: '17%',
      minWidth: '110px',
    },
    {
      key: 'updated_date',
      render: ({ updated_date }) => <DateCell date={updated_date} />,
      title: t('Last Modified'),
      width: '12%',
      minWidth: '100px',
    },
    {
      key: 'created_date',
      render: ({ created_date }) => <DateCell date={created_date} />,
      title: t('Created'),
      width: '12%',
      minWidth: '110px',
      hide: true,
    },
  ];
};
